import React, { memo } from 'react'
import { useFormikContext } from 'formik'
import tw from 'twin.macro'

export interface ProtoRadioOption {
  label: string
  value: string
}

export interface ProtoRadioGroupProps<T> {
  name: keyof T & string
  options: ProtoRadioOption[]
  disabled?: boolean
}

const ProtoRadioGroupComponent = <T extends object>({
  name,
  options,
  disabled = false,
}: ProtoRadioGroupProps<T>) => {
  const { values, setFieldValue } = useFormikContext<T>()
  const selected = values[name] as unknown as string

  return (
    <div role="radiogroup" css={tw`flex items-center gap-6`}>
      {options.map((option) => {
        const checked = option.value === selected

        return (
          <div
            key={option.value}
            role="radio"
            aria-checked={checked}
            aria-disabled={disabled}
            tabIndex={disabled ? -1 : 0}
            onClick={() => {
              if (!disabled) {
                setFieldValue(name, option.value)
              }
            }}
            onKeyDown={(event) => {
              if (disabled) {
                return
              }

              if (event.key === ' ' || event.key === 'Enter') {
                event.preventDefault()
                setFieldValue(name, option.value)
              }
            }}
            css={[
              tw`flex items-center gap-2 cursor-pointer select-none`,
              disabled && tw`cursor-not-allowed opacity-[0.85]`,
            ]}
          >
            <div
              css={[
                tw`box-border flex h-[18px] w-[18px] flex-shrink-0 items-center justify-center rounded-full border-2 border-solid transition-all duration-[120ms]`,
                checked ? tw`border-shades-1000` : tw`border-shades-300 bg-shades-0`,
              ]}
            >
              {checked && <div css={tw`h-[8px] w-[8px] rounded-full bg-shades-1000`} />}
            </div>
            <span css={tw`text-sm text-shades-800`}>{option.label}</span>
          </div>
        )
      })}
    </div>
  )
}

export const ProtoRadioGroup = memo(ProtoRadioGroupComponent) as typeof ProtoRadioGroupComponent
